import dayjs from 'dayjs';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { OpenReservationBase } from '../open-reservation-base.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';

export class OpenReservationDeletedEvent extends NotificationEvent {
  static readonly TYPE_ID: string = 'cb022201d0a640aebebd5308cd92b588';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly reservation: OpenReservationBase,
    public readonly reservationId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenReservationDeletedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OpenReservationDeletedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenReservationDeletedEvent {
    return new OpenReservationDeletedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<OpenReservationBase>(MapUtils.get<SerializedData>(map, 'reservation')),
        MapUtils.get<string>(map, 'reservationId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'reservationId': this.reservationId,
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'reservation': context.toData(this.reservation),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new OpenReservationDeletedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.reservation.clone(),
        this.reservationId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen &&
        isSameData(other.reservation, this.reservation) &&
        other.reservationId === this.reservationId;
  }

  markAsSeen(): this {
    return new OpenReservationDeletedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.reservation.clone(),
        this.reservationId,
    ) as this;
  }
}

export class OpenReservationDeletedEventBuilder extends DataModelBuilder<OpenReservationDeletedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenReservationDeletedEvent {
    return OpenReservationDeletedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OpenReservationDeletedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenReservationDeletedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
